import { gql } from '@apollo/client';
import IconArrowRight from '@apollo/icons/small/IconArrowRight.svg';
import IconHide from '@apollo/icons/small/IconHide.svg';
import IconInfo from '@apollo/icons/small/IconInfo.svg';
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Code,
  Pagination,
  StatusBadge,
  StatusBadgeProps,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@apollo/orbit';
import classnames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
// TODO: make this a shared component
import { SearchFilter } from 'src/app/graph/operationsPage/insightsL3Nav/insightsList/searchFilter/SearchFilter';
import { OperationStatus } from 'src/app/graph/routes';
import { HighlightedGraphQLName } from 'src/components/highlightedGraphQLName/HighlightedGraphQLName';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import Config from 'src/lib/config';
import { explainRegexMatch } from 'src/lib/explainRegexMatch';
import { format } from 'src/lib/format';
import { AffectedQuery, OperationDetails } from 'src/lib/graphqlTypes/types';
import { mergeQueryParams } from 'src/lib/routing';

import { NoSearchResults } from './NoSearchResults';
import { OperationIdPopover } from './OperationIdPopover';
import { OperationStatusMenu } from './OperationStatusMenu';

const OPERATIONS_LIST_PAGE_SIZE = 50;

export const UnaffectedOperationsFragment = gql`
  fragment UnaffectedOperationsFragment on OperationsCheckResult {
    id
    unaffectedOperations {
      id
      name
      signature
    }
  }
`;

export type AffectedOperation = Omit<AffectedQuery, 'changes' | 'displayName'>;

export type StatusMenuOptions = {
  text: string;
  value: OperationStatus;
};

export type AllOperations = (AffectedOperation | OperationDetails)[];

const operationStatusTextMap = {
  broken: 'Broken',
  'potentially-affected': 'Potentially Affected',
  'marked-as-safe': 'Safe',
  ignored: 'Ignored',
  unaffected: 'Unaffected',
} satisfies Record<OperationStatus, string>;

const paginate = (
  operations: AllOperations,
  pageSize: number,
  pageNumber: number,
) =>
  operations.slice(
    (pageNumber - 1) * pageSize,
    (pageNumber - 1) * pageSize + pageSize,
  );

export const getOperationStatus = (
  operation: AffectedOperation | OperationDetails,
): OperationStatus => {
  if (operation.__typename === 'AffectedQuery') {
    const { isValid, markedAsSafe, markedAsIgnored } = operation;
    return markedAsSafe
      ? 'marked-as-safe'
      : markedAsIgnored
        ? 'ignored'
        : isValid
          ? 'potentially-affected'
          : 'broken';
  } else {
    return 'unaffected';
  }
};

export const getAffectedOperationStatusBadge = (
  operationStatus: OperationStatus,
  variant: StatusBadgeProps['variant'],
) => {
  switch (operationStatus) {
    case 'broken':
      return (
        <StatusBadge variant={variant} colorScheme="red">
          Broken
        </StatusBadge>
      );
    case 'potentially-affected':
      return (
        <StatusBadge variant={variant} colorScheme="yellow">
          Potentially affected
        </StatusBadge>
      );
    case 'marked-as-safe':
      return (
        <StatusBadge variant={variant} colorScheme="blue">
          Marked as safe
        </StatusBadge>
      );
    case 'ignored':
      return (
        <StatusBadge variant={variant} icon={<IconHide />}>
          Ignored
        </StatusBadge>
      );
    case 'unaffected':
      return (
        <StatusBadge variant={variant} colorScheme="gray">
          Unaffected
        </StatusBadge>
      );
    default:
      assertUnreachable(operationStatus);
  }
};

export const OperationsCheckList = ({
  pageFromUrl = 1,
  statusFilter = [],
  search = '',
  allOperations,
  searchedAndFilteredResults,
  setOperationForDrawer,
}: {
  pageFromUrl?: number;
  search?: string;
  statusFilter?: OperationStatus[];
  allOperations: (AffectedOperation | OperationDetails)[];
  searchedAndFilteredResults: (AffectedOperation | OperationDetails)[];
  setOperationForDrawer: (
    operation: AffectedOperation | OperationDetails,
  ) => void;
}) => {
  const history = useHistory();
  const location = useLocation();
  const { currentOperationsCheckDetailsRoute } = useInternalGraphLinking();

  const createLinkToOperation = useCallback(
    (operationId: string) => {
      history.push({
        ...location,
        search: mergeQueryParams(location.search, {
          [Config.queryParameters.SelectedQueryId]: operationId,
        }),
      });
    },
    [location, history],
  );

  const getOverrideText = (operation: AffectedOperation) => {
    if (operation.alreadyApproved && !operation.markedAsSafe)
      return 'Marked as safe';
    if (operation.alreadyIgnored && !operation.markedAsIgnored)
      return 'Marked as ignored';
    if (!operation.alreadyApproved && operation.markedAsSafe)
      return 'Unmarked as safe';
    if (!operation.alreadyIgnored && operation.markedAsIgnored)
      return 'Unignored';
  };

  const updateUrlWithParams = (patch: {
    page?: number;
    search?: string;
    statusFilter?: OperationStatus[];
  }) => {
    history.push(
      currentOperationsCheckDetailsRoute.locationFrom({ location, patch }),
    );
  };

  const [operationSearchInputValue, setOperationSearchInputValue] =
    useState('');

  useEffect(() => {
    if (search) setOperationSearchInputValue(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearSearch = () => {
    setOperationSearchInputValue('');
    updateUrlWithParams({
      search: '',
      page: 1,
    });
  };

  const statusOptions = allOperations.reduce(
    (
      acc: StatusMenuOptions[],
      operation: AffectedOperation | OperationDetails,
    ) => {
      const operationStatus = getOperationStatus(operation);
      const statusInAcc = acc.find(
        (statusOption) => operationStatus === statusOption.value,
      );
      if (statusInAcc) return acc;

      return acc.concat({
        value: operationStatus,
        text: operationStatusTextMap[operationStatus],
      });
    },
    [],
  );

  const operations = paginate(
    searchedAndFilteredResults,
    OPERATIONS_LIST_PAGE_SIZE,
    pageFromUrl,
  );
  const totalCount = searchedAndFilteredResults.length
    ? searchedAndFilteredResults.length
    : allOperations.length;

  return (
    <Card>
      <CardHeader>
        <SearchFilter
          className="min-w-[248px] max-w-1/2"
          placeholderText="Search by operation name or ID"
          inputValue={operationSearchInputValue}
          onInputChange={setOperationSearchInputValue}
          onSearch={(searchTerm: string) => {
            // onSearch gets called when input changes. This prevents updating searchResults with the entire list of operations
            if (searchTerm === '') {
              clearSearch();
            } else {
              // reset currentPage (ie. if user in page 10, then searches, they should come to page 1 of search results)
              updateUrlWithParams({
                search: searchTerm,
                page: pageFromUrl > 1 ? 1 : pageFromUrl,
              });
            }
          }}
          onClear={clearSearch}
        />
        <ButtonGroup>
          <OperationStatusMenu
            statusOptions={statusOptions}
            selectedOptions={statusFilter}
            handleOnChange={(value) => {
              updateUrlWithParams({
                statusFilter: value,
                page: pageFromUrl > 1 ? 1 : pageFromUrl,
              });
            }}
            handleClearAll={() => {
              updateUrlWithParams({
                statusFilter: [],
                page: pageFromUrl > 1 ? 1 : pageFromUrl,
              });
            }}
          />
          <Button
            onClick={() => {
              setOperationSearchInputValue('');
              updateUrlWithParams({ statusFilter: [], page: 1, search: '' });
            }}
          >
            Reset filters
          </Button>
        </ButtonGroup>
      </CardHeader>
      <CardBody
        className={classnames(
          'max-h-[calc(100vh-430px)]',
          !operations.length && 'justify-center',
        )}
      >
        {!operations.length ? (
          <NoSearchResults />
        ) : (
          <TableContainer className="hide-scrollbar">
            <Table
              isInteractive
              layout="fixed"
              size="condensed"
              variant="striped"
              className="w-auto min-w-full"
            >
              <Thead>
                <Tr>
                  <Th className="w-[400px]">Operation</Th>
                  <Th className="w-[230px]">Status</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {operations.map((operation) => {
                  const { id, name, signature } = operation;
                  const operationStatus = getOperationStatus(operation);
                  const operationStatusBadge = getAffectedOperationStatusBadge(
                    operationStatus,
                    'hidden',
                  );
                  const regex = name
                    ? RegExp(`(${operationSearchInputValue})`, 'i').exec(name)
                    : null;
                  const overrideText =
                    operation.__typename === 'AffectedQuery'
                      ? getOverrideText(operation)
                      : null;
                  return (
                    <Tr
                      key={id}
                      className="group cursor-pointer"
                      tabIndex={0}
                      onClick={() => {
                        if (operationStatus === 'unaffected') {
                          setOperationForDrawer(operation);
                        } else {
                          createLinkToOperation(id);
                        }
                      }}
                    >
                      <Td>
                        <div className="flex max-w-[367px]">
                          {name ? (
                            <HighlightedGraphQLName
                              singleLine
                              className="truncate"
                              name={name}
                              regexResult={explainRegexMatch(regex)}
                            />
                          ) : (
                            <span className="italic text-secondary">
                              Unnamed Operation
                            </span>
                          )}
                          <OperationIdPopover
                            operationId={id}
                            signature={signature}
                            triggerElement={
                              <Code
                                variant="mono"
                                className="ml-2 cursor-pointer"
                              >
                                {format.shortQueryId(id)}
                              </Code>
                            }
                            handleOnClick={() =>
                              setOperationForDrawer(operation)
                            }
                          />
                        </div>
                      </Td>
                      <Td>
                        <div className="flex items-center">
                          {operationStatusBadge}
                          {overrideText && (
                            <div className="ml-2 flex">
                              <p className="mt-0.5 text-xs">({overrideText})</p>
                              <Tooltip label="Rerun this check to see your changes">
                                <IconInfo className="ml-2 mt-1 flex-none" />
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      </Td>
                      <Td>
                        <div className="invisible flex min-w-[180px] items-center justify-end px-2 group-hover:visible">
                          <Text variant="bodySb">
                            {operationStatus === 'unaffected'
                              ? 'View Operation Body'
                              : 'View Changes'}
                          </Text>
                          <IconArrowRight className="ml-2 mt-0.75" />
                        </div>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </CardBody>
      {operations.length > 0 && (
        <CardFooter>
          <Pagination
            currentPage={pageFromUrl}
            totalCount={totalCount}
            pageSize={OPERATIONS_LIST_PAGE_SIZE}
            onChangePage={(page) => {
              updateUrlWithParams({ page });
            }}
          />
        </CardFooter>
      )}
    </Card>
  );
};
